import i18next from 'i18next';
import { resolve, reorderPermutation } from '@galkahana/unicode-bidirectional';
import { Color } from '@monsterverse/math';
import * as pc from 'playcanvas';
import Backend from 'i18next-http-backend';
import BackendAdapter from 'i18next-multiload-backend-adapter';

function isRTLLocale() {
  return i18next.dir() === "rtl";
}
function getLanguage() {
  return i18next.language;
}
function isArabic() {
  return i18next.language === "ar-SA";
}

function playcanvasRTLReorder(symbols) {
  // Convert array of symbols to codepoints
  const codes = symbols.map(function (s) {
    return s.codePointAt(0);
  });
  // resolve levels and generate mapping
  const levels = resolve(codes, 0, true);
  const indices = reorderPermutation(levels);
  const rtl = levels[0] === 1;
  return {
    rtl,
    mapping: rtl ? indices.reverse() : indices
  };
}

/**
 * Font asset overrides.
 * If not listed here, playcanvas will use the font configured in the playcanvas project.
 */
const LANG_FONT_MAP = {
  "ar-SA": "Arial-Bold.ttf",
  "th-TH": "NotoSerifThai-Bold.ttf"
};
/**
 * List of languages that are supported using CanvasFont, which renders
 * glyphs on demand in an HTML off-screen canvas.
 */
const LANG_CANVAS_FONT_MAP = {
  "ja-JP": true,
  "ko-KR": true,
  "ru-RU": true,
  "vi-VN": true,
  "uk-UA": true,
  "zh-CN": true,
  "zh-HK": true,
  "zh-TW": true,
  // these are not supported by canvas fonts due to issues with rendering individual characters,
  // they need to use specific font file that supports it
  "ar-SA": false,
  "th-TH": false
};
let fontAsset;
let canvasFont;
/** Prepares `element` for PlayCanvas text i18n rendering.
 * * Sets `.rtlReorder` property to `isRTLLocale()`
 * * If `supportsCanvasFont(getLanguage())`, sets `element.font` to `initializeCanvasFont()`,
 * which initializes the CanvasFont if not already done;
 * * else, sets `.font` to `app.assets.find(getFontForLanguage(getLanguage()), "font")`, if found.
 *
 * Note: Does ***not*** add characters already in element text to CanvasFont.
 * Call `processTextForDisplayInPlaycanvasTextElement` whenever setting text. */
const playcanvasSetupLanguageFontsSupport = element => {
  const app = pc.AppBase.getApplication();
  if (!element || !app) return;
  element.rtlReorder = isRTLLocale();
  const language = getLanguage();
  if (supportsCanvasFont(language)) {
    // use CanvasFont instead of pre-selected default font
    const font = initializeCanvasFont();
    if (font) {
      element.font = font;
      return;
    }
  }
  const fontName = getFontForLanguage(language);
  if (!fontName) {
    // just use default font pre-selected in playcanvas project
    return;
  }
  if (!fontAsset) {
    fontAsset = app.assets.find(fontName, "font");
  }
  if (fontAsset) {
    // change to use the specified font asset instead of the pre-selected default one
    element.fontAsset = fontAsset._id;
  }
};
function getFontForLanguage(language) {
  return LANG_FONT_MAP[language];
}
function supportsCanvasFont(language) {
  return LANG_CANVAS_FONT_MAP[language] || false;
}
function initializeCanvasFont() {
  if (!canvasFont) {
    const app = pc.AppBase.getApplication();
    if (!app) {
      return;
    }
    canvasFont = new pc.CanvasFont(app, {
      color: Color.WHITE.clone(),
      fontWeight: "bold",
      fontName: "OmoType, Helvetica Neue, Helvetica, Arial, sans-serif",
      fontSize: 48,
      width: 1024,
      height: 1024,
      padding: 12
    });
    // pre-render some base latin characters
    canvasFont.createTextures(" !,.:-0123456789?abcdefghijklmnopqrstuvwxyz！");
  }
  return canvasFont;
}
function updateCanvasFontCharacters(text) {
  if (canvasFont) {
    canvasFont.updateTextures(text);
  }
}

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

/**
 * Converter from Arabic to Arabic Presentation Forms-B
 * http://en.wikipedia.org/wiki/Arabic_(Unicode_block)
 * http://en.wikipedia.org/wiki/Arabic_Presentation_Forms-B
 *
 * Usage:
 * var arabicFormBText = arabicConverter.convertArabic(arabicText);
 * var arabicText = convertArabicBack.convertArabic(arabicFormBText);
 *
 * @link https://github.com/lamerw/Arabic-Converter-From-and-To-Arabic-Presentation-Forms-B
 */
/**
 * @private
 * @type {number}
 */
const NIL = 0x0000;
/**
 * @TODO replace with charsMap.length
 * @private
 * @type {number}
 */
const MAP_LENGTH = 37;
/**
 * @TODO replace with combCharsMap.length
 * @private
 * @type {number}
 */
const COMB_MAP_LENGTH = 4;
/**
 * @TODO replace with transChars.length
 * @private
 * @type {number}
 */
const TRANS_CHARS_LENGTH = 39;
/**
 * TODO refactor charsMap for the following structure
 * @private
 * @type {{code: number, mIsolated: number, mInitial: number, mMedial: number, mFinal: number}}
 */
//var CharRep = {
//    code: 0,
//    mIsolated: 0,
//    mInitial: 0,
//    mMedial: 0,
//    mFinal: 0
//};
/**
 * TODO refactor combCharsMap for the following structure
 * @private
 * @type {{code: number, mIsolated: number, mInitial: number, mMedial: number, mFinal: number}}
 */
//var CombCharRep = {
//    code: 0,
//    mIsolated: 0,
//    mInitial: 0,
//    mMedial: 0,
//    mFinal: 0
//};
/**
 * @private
 * @type {*[]}
 */
const charsMap = [[0x0621, 0xfe80, NIL, NIL, NIL] /* HAMZA */, [0x0622, 0xfe81, NIL, NIL, 0xfe82] /* ALEF_MADDA */, [0x0623, 0xfe83, NIL, NIL, 0xfe84] /* ALEF_HAMZA_ABOVE */, [0x0624, 0xfe85, NIL, NIL, 0xfe86] /* WAW_HAMZA */, [0x0625, 0xfe87, NIL, NIL, 0xfe88] /* ALEF_HAMZA_BELOW */, [0x0626, 0xfe89, 0xfe8b, 0xfe8c, 0xfe8a] /* YEH_HAMZA */, [0x0627, 0xfe8d, NIL, NIL, 0xfe8e] /* ALEF */, [0x0628, 0xfe8f, 0xfe91, 0xfe92, 0xfe90] /* BEH */, [0x0629, 0xfe93, NIL, NIL, 0xfe94] /* TEH_MARBUTA */, [0x062a, 0xfe95, 0xfe97, 0xfe98, 0xfe96] /* TEH */, [0x062b, 0xfe99, 0xfe9b, 0xfe9c, 0xfe9a] /* THEH */, [0x062c, 0xfe9d, 0xfe9f, 0xfea0, 0xfe9e] /* JEEM */, [0x062d, 0xfea1, 0xfea3, 0xfea4, 0xfea2] /* HAH */, [0x062e, 0xfea5, 0xfea7, 0xfea8, 0xfea6] /* KHAH */, [0x062f, 0xfea9, NIL, NIL, 0xfeaa] /* DAL */, [0x0630, 0xfeab, NIL, NIL, 0xfeac] /* THAL */, [0x0631, 0xfead, NIL, NIL, 0xfeae] /* REH */, [0x0632, 0xfeaf, NIL, NIL, 0xfeb0] /* ZAIN */, [0x0633, 0xfeb1, 0xfeb3, 0xfeb4, 0xfeb2] /* SEEN */, [0x0634, 0xfeb5, 0xfeb7, 0xfeb8, 0xfeb6] /* SHEEN */, [0x0635, 0xfeb9, 0xfebb, 0xfebc, 0xfeba] /* SAD */, [0x0636, 0xfebd, 0xfebf, 0xfec0, 0xfebe] /* DAD */, [0x0637, 0xfec1, 0xfec3, 0xfec4, 0xfec2] /* TAH */, [0x0638, 0xfec5, 0xfec7, 0xfec8, 0xfec6] /* ZAH */, [0x0639, 0xfec9, 0xfecb, 0xfecc, 0xfeca] /* AIN */, [0x063a, 0xfecd, 0xfecf, 0xfed0, 0xfece] /* GHAIN */, [0x0640, 0x0640, NIL, NIL, NIL] /* TATWEEL */, [0x0641, 0xfed1, 0xfed3, 0xfed4, 0xfed2] /* FEH */, [0x0642, 0xfed5, 0xfed7, 0xfed8, 0xfed6] /* QAF */, [0x0643, 0xfed9, 0xfedb, 0xfedc, 0xfeda] /* KAF */, [0x0644, 0xfedd, 0xfedf, 0xfee0, 0xfede] /* LAM */, [0x0645, 0xfee1, 0xfee3, 0xfee4, 0xfee2] /* MEEM */, [0x0646, 0xfee5, 0xfee7, 0xfee8, 0xfee6] /* NOON */, [0x0647, 0xfee9, 0xfeeb, 0xfeec, 0xfeea] /* HEH */, [0x0648, 0xfeed, NIL, NIL, 0xfeee] /* WAW */,
//[ 0x0649, 0xFEEF, 0xFBE8, 0xFBE9, 0xFEF0 ],    /* ALEF_MAKSURA */
[0x0649, 0xfeef, NIL, NIL, 0xfef0] /* ALEF_MAKSURA */, [0x064a, 0xfef1, 0xfef3, 0xfef4, 0xfef2] /* YEH */];
/**
 * @private
 * @type {*[]}
 */
const combCharsMap = [[[0x0644, 0x0622], 0xfef5, NIL, NIL, 0xfef6] /* LAM_ALEF_MADDA */, [[0x0644, 0x0623], 0xfef7, NIL, NIL, 0xfef8] /* LAM_ALEF_HAMZA_ABOVE */, [[0x0644, 0x0625], 0xfef9, NIL, NIL, 0xfefa] /* LAM_ALEF_HAMZA_BELOW */, [[0x0644, 0x0627], 0xfefb, NIL, NIL, 0xfefc] /* LAM_ALEF */];
const transChars = [0x0610 /* ARABIC SIGN SALLALLAHOU ALAYHE WASSALLAM */, 0x0612 /* ARABIC SIGN ALAYHE ASSALLAM */, 0x0613 /* ARABIC SIGN RADI ALLAHOU ANHU */, 0x0614 /* ARABIC SIGN TAKHALLUS */, 0x0615 /* ARABIC SMALL HIGH TAH */, 0x064b /* ARABIC FATHATAN */, 0x064c /* ARABIC DAMMATAN */, 0x064d /* ARABIC KASRATAN */, 0x064e /* ARABIC FATHA */, 0x064f /* ARABIC DAMMA */, 0x0650 /* ARABIC KASRA */, 0x0651 /* ARABIC SHADDA */, 0x0652 /* ARABIC SUKUN */, 0x0653 /* ARABIC MADDAH ABOVE */, 0x0654 /* ARABIC HAMZA ABOVE */, 0x0655 /* ARABIC HAMZA BELOW */, 0x0656 /* ARABIC SUBSCRIPT ALEF */, 0x0657 /* ARABIC INVERTED DAMMA */, 0x0658 /* ARABIC MARK NOON GHUNNA */, 0x0670 /* ARABIC LETTER SUPERSCRIPT ALEF */, 0x06d6 /* ARABIC SMALL HIGH LIGATURE SAD WITH LAM WITH ALEF MAKSURA */, 0x06d7 /* ARABIC SMALL HIGH LIGATURE QAF WITH LAM WITH ALEF MAKSURA */, 0x06d8 /* ARABIC SMALL HIGH MEEM INITIAL FORM */, 0x06d9 /* ARABIC SMALL HIGH LAM ALEF */, 0x06da /* ARABIC SMALL HIGH JEEM */, 0x06db /* ARABIC SMALL HIGH THREE DOTS */, 0x06dc /* ARABIC SMALL HIGH SEEN */, 0x06df /* ARABIC SMALL HIGH ROUNDED ZERO */, 0x06e0 /* ARABIC SMALL HIGH UPRIGHT RECTANGULAR ZERO */, 0x06e1 /* ARABIC SMALL HIGH DOTLESS HEAD OF KHAH */, 0x06e2 /* ARABIC SMALL HIGH MEEM ISOLATED FORM */, 0x06e3 /* ARABIC SMALL LOW SEEN */, 0x06e4 /* ARABIC SMALL HIGH MADDA */, 0x06e7 /* ARABIC SMALL HIGH YEH */, 0x06e8 /* ARABIC SMALL HIGH NOON */, 0x06ea /* ARABIC EMPTY CENTRE LOW STOP */, 0x06eb /* ARABIC EMPTY CENTRE HIGH STOP */, 0x06ec /* ARABIC ROUNDED HIGH STOP WITH FILLED CENTRE */, 0x06ed /* ARABIC SMALL LOW MEEM */];
/**
 * TODO rename into camelCase
 * @private
 *
 * @param code
 * @returns {boolean}
 */
function characterMapContains(code) {
  for (let i = 0; i < MAP_LENGTH; i++) {
    //[0] == .code
    if (charsMap[i][0] === code) {
      return true;
    }
  }
  return false;
}
/**
 * TODO rename into camelCase
 * @private
 *
 * @param code
 * @returns {CharRep}
 */
function getCharRep(code) {
  for (let i = 0; i < MAP_LENGTH; i++) {
    //[0] == .code
    if (charsMap[i][0] === code) {
      return charsMap[i];
    }
  }
  //FIXME CharRep object
  return [NIL, NIL, NIL, NIL];
}
/**
 * @TODO rename into camelCase
 * @private
 *
 * @param code1
 * @param code2
 * @returns {CombCharRep}
 */
function getCombCharRep(code1, code2) {
  for (let i = 0; i < COMB_MAP_LENGTH; i++) {
    //[0] == .code
    if (combCharsMap[i][0][0] === code1 && combCharsMap[i][0][1] === code2) {
      return combCharsMap[i];
    }
  }
  //FIXME CombCharRep object
  return [[NIL, NIL], NIL, NIL, NIL, NIL];
}
/**
 * @TODO rename into camelCase
 * @private
 *
 * @param code
 * @returns {boolean}
 */
function isTransparent(code) {
  for (let i = 0; i < TRANS_CHARS_LENGTH; i++) {
    if (transChars[i] === code) {
      return true;
    }
  }
  return false;
}
/**
 * convert to Arabic Presentation Forms B
 * @param normal
 * @returns {string}
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
function convertArabic(normal) {
  if (!normal) {
    return "";
  }
  const len = normal.length;
  /* typeof CharRep*/
  let crep;
  /* typeof CombCharRep*/
  let combcrep;
  const shaped = [];
  let writeCount = 0;
  for (let i = 0; i < len; i++) {
    const current = normal.charCodeAt(i);
    if (characterMapContains(current)) {
      let prev = NIL;
      let next = NIL;
      let prevID = i - 1;
      let nextID = i + 1;
      /*
                 Transparent characters have no effect in the shaping process.
                 So, ignore all the transparent characters that are BEFORE the
                 current character.
                 */
      for (; prevID >= 0; prevID--) {
        if (!isTransparent(normal.charCodeAt(prevID))) {
          break;
        }
      }
      //[2] == .mInitial
      //[3] == .mMedial
      if (prevID < 0 || !characterMapContains(prev = normal.charCodeAt(prevID)) || (crep = getCharRep(prev))[2] === NIL && crep[3] === NIL) {
        prev = NIL;
      }
      /*
                 Transparent characters have no effect in the shaping process.
                 So, ignore all the transparent characters that are AFTER the
                 current character.
                 */
      for (; nextID < len; nextID++) {
        if (!isTransparent(normal.charCodeAt(nextID))) {
          break;
        }
      }
      //[3] == .mMedial
      //[4] == .mFinal
      if (nextID >= len || !characterMapContains(next = normal.charCodeAt(nextID)) || (crep = getCharRep(next))[3] === NIL && (crep = getCharRep(next))[4] === NIL && next !== 0x0640) {
        next = NIL;
      }
      /* Combinations */
      if (current === 0x0644 && next !== NIL && (next === 0x0622 || next === 0x0623 || next === 0x0625 || next === 0x0627)) {
        combcrep = getCombCharRep(current, next);
        if (prev !== NIL) {
          //[4] == .mFinal
          shaped[writeCount++] = combcrep[4];
        } else {
          //[1] == .mIsolated
          shaped[writeCount++] = combcrep[1];
        }
        i++;
        continue;
      }
      crep = getCharRep(current);
      /* Medial */
      //[3] == .mMedial
      if (prev !== NIL && next !== NIL && crep[3] !== NIL) {
        shaped[writeCount++] = crep[3];
        continue;
        /* Final */
      }
      //[4] == .mFinal
      else if (prev !== NIL && crep[4] !== NIL) {
        //[4] == .mFinal
        shaped[writeCount++] = crep[4];
        continue;
        /* Initial */
      }
      //[2] == .mInitial
      else if (next !== NIL && crep[2] !== NIL) {
        shaped[writeCount++] = crep[2];
        continue;
      }
      /* Isolated */
      //[1] == .mIsolated
      shaped[writeCount++] = crep[1];
    } else {
      shaped[writeCount++] = current;
    }
  }
  shaped[writeCount] = NIL;
  let toReturn = "";
  for (let d = 0; d < writeCount; d++) {
    if (typeof shaped[d] !== "undefined") {
      //toReturn += shaped[d] +' ';
      toReturn += String.fromCharCode(shaped[d]);
    }
  }
  return toReturn;
}

var common = {
	disclaimer: "DEMO - NOT FOR DISTRIBUTION",
	actions: {
		yes: "Yes",
		no: "No",
		ok: "Ok",
		cancel: "Cancel",
		close: "Close",
		discard: "Discard",
		retry: "Retry"
	},
	"new": "New",
	snap: "Snap",
	loading: {
		loading_island: "Loading island…",
		loading_monsters: "Loading monsters…",
		loading_finished: "Finishing loading…"
	},
	confirmation: {
		return_app: "Exit Island?"
	},
	guest: {
		name: "__name__'s Friend",
		banner_text: "Playing as __name__"
	},
	player: "Player",
	playgrounds: {
		sign_title: "Global Playground",
		sign_prompt: "Join?",
		sign_prompt_in_progress: "Game in progress",
		open_time: "Opens in",
		matchmaking: {
			looking_for_players: "Looking for players...",
			fishing_for_folks: "Fishing for folks...",
			checking_islands: "Checking other islands...",
			pinging_pigeons: "Pinging the pigeons...",
			smoke_signals: "Sending smoke signals...",
			seeing_who_out_there: "Seeing who's out there...",
			searching_seas: "Searching the seas...",
			casting_company: "Casting for company...",
			calling_monsters: "Calling for monsters...",
			rounding_crew: "Rounding up a crew...",
			scanning_sails: "Scanning for sails...",
			scouting_shores: "Scouting the shores...",
			lighting_signal: "Lighting a signal fire...",
			sniffing_wind: "Sniffing the wind...",
			scanning_horizon: "Scanning the horizon...",
			seeking_players: "Seeking fellow players...",
			looking_beyond: "Looking beyond...",
			waving_hilltop: "Waving from the hilltop..",
			game_in_progress: "Game in progress...",
			sending_invites: "Sending invites...",
			peeking_under_rocks: "Peeking under rocks...",
			gathering_goofballs: "Gathering goofballs...",
			tracking_down_teammates: "Tracking down teammates...",
			assembling_adventurers: "Assembling adventurers...",
			sailing_for_sidekicks: "Sailing for sidekicks...",
			calling_all_castaways: "Calling all castaways...",
			scanning_sky: "Scanning the sky...",
			getting_gang_together: "Getting the gang together...",
			gametime: "Gametime!",
			waiting_for_crew: "Waiting for crew...",
			get_ready: "Get ready!"
		},
		modal: {
			title: "Global Playground",
			description: "Join a game with players from all around the world",
			join: "Join",
			join_next_game: "Join next game"
		}
	},
	voting: {
		title: "Vote for next game",
		cast_vote: "Cast vote"
	},
	play_activity: {
		title: "Play Activity?",
		new_game: "New Game",
		new_map: "New Map",
		yes: "Yes",
		no: "No",
		total_players: "Total players",
		not_enough_players_joined: "Not enough players joined!",
		not_enough_players: "Not enough players are available",
		opt_in_late: "Joined too late!",
		in_progress: "The activity is already in progress!",
		ended_player_left: "Not enough players",
		sign_title: "Arena",
		sign_prompt: "Start a game?",
		sign_map_prompt: "Pick a map",
		available_players: "__count__ available",
		need_more_players: "Need __count__ more",
		invite: "Invite",
		time_left: "Time left",
		team_win: "__teamName__ team wins!",
		winner: "Winner!",
		next_game: "Next game",
		play_again: "Play again",
		next_activity: "Next: __game__",
		play_next: "Join next game",
		next_pg_activity: "Next Game...",
		gathering_votes: "Gathering votes...",
		opt_out: "I'm out",
		start_button: "Start",
		upcoming_activity: "More tag coming soon...",
		more_games: "More games on Home Islands!",
		go: "Go!",
		times_up: "Time's up!",
		game_over: "Game over!",
		tag: {
			activity_name: "Chicken Tag",
			activity_description: "Regular tag, but as chickens.",
			invite: "Play Chicken Tag?",
			team_name_it: "Chasers",
			team_name_runner: "Runners",
			you_are_tagger: "Tag Somebody!",
			you_are_runner: "Run away!"
		},
		zombietag: {
			activity_name: "Zombie Tag",
			activity_description: "Run away or join the horde.",
			invite: "Play Zombie Tag?",
			team_name_it: "Zombies",
			team_name_runner: "Runners",
			you_are_tagger: "Tag Somebody!",
			you_are_runner: "Run away!"
		},
		freezetag: {
			activity_name: "Freeze Tag",
			activity_description: "Escape the yeti and save your friends.",
			invite: "Play Freeze Tag?",
			team_name_it: "Freezers",
			team_name_runner: "Runners",
			you_are_tagger: "Freeze Somebody!",
			you_are_runner: "Run away!",
			you_are_frozen: "You are frozen!"
		},
		hideseek: {
			activity_name: "Hide and Seek",
			activity_description: "Vanish, sneak, and don't get caught.",
			invite: "Play Hide and Seek?",
			team_name_it: "Seekers",
			team_name_runner: "Hiders",
			you_are_tagger: "Seek!",
			you_are_runner: "Hide!",
			it_nameplate: "Seeker",
			runner_nameplate: "Hider"
		},
		cannon_basketball: {
			activity_name: "Capture the Cannons",
			activity_description: "Make shots. Capture cannons.",
			invite: "Play Capture the Cannons?",
			prompt: "Make Shots!",
			team_name_yellow: "Yellow Team",
			team_name_blue: "Blue Team",
			team_name_tie: "Tie!",
			notif_score: "__playerName__ captured a cannon!",
			notif_score_team: "Your team captured a cannon!",
			notif_score_self: "You captured a cannon!"
		},
		blastsplash: {
			activity_name: "Blast & Splash",
			activity_description: "Launch, splash, and cover the map.",
			invite: "Play Blast & Splash?",
			prompt: "Paint!",
			team_name_yellow: "Yellow Team",
			team_name_blue: "Blue Team",
			team_name_tie: "Tie!"
		},
		paintparty: {
			activity_name: "Paint Party",
			activity_description: "Your party, your masterpiece.",
			invite: "Play Paint Party?",
			prompt: "Paint!"
		},
		playground: {
			activity_name: "Playgrounds",
			invite: "Join Global Playground"
		},
		monsterball: {
			activity_name: "Monster Ball",
			activity_description: "Dash, kick, and score big.",
			invite: "Play Monster Ball?",
			prompt: "Score goals!",
			team_name_yellow: "Yellow Team",
			team_name_blue: "Blue Team",
			team_name_tie: "Tie!"
		},
		dolphinpolo: {
			activity_name: "Dolphin Polo",
			activity_description: "Dive, pass, and shoot to win.",
			invite: "Play Dolphin Polo?",
			prompt: "Score goals!",
			team_name_yellow: "Yellow Team",
			team_name_blue: "Blue Team",
			team_name_tie: "Tie!"
		},
		awards: {
			tag: {
				relentless: {
					title: "Relentless",
					description: "Tagged the most players"
				}
			},
			zombietag: {
				infectious: {
					title: "Infectious",
					description: "Tagged the most players"
				},
				last_survivor: {
					title: "Last Survivor",
					description: "Last to be zombified"
				}
			},
			freezetag: {
				frigid: {
					title: "Frigid",
					description: "Froze the most players"
				},
				last_survivor: {
					title: "Last Survivor",
					description: "Last to be frozen"
				},
				defroster: {
					title: "Defroster",
					description: "Unfroze the most players"
				}
			},
			hideseek: {
				eagle_eye: {
					title: "Eagle Eye",
					description: "Found the most players"
				},
				last_found: {
					title: "Last Found",
					description: "Last to be found"
				}
			},
			cannon_basketball: {
				scoring_machine: {
					title: "Scoring Machine",
					description: "Most points scored"
				},
				careful: {
					title: "Careful",
					description: "Most time spent aiming"
				},
				quick_shot: {
					title: "Quick Shot",
					description: "Most shots taken"
				},
				on_fire: {
					title: "On Fire",
					description: "Most shots made in a row"
				},
				most_accurate: {
					title: "Accurate",
					description: "Highest shot percentage"
				},
				nothing_but_net: {
					title: "Nothing But Net",
					description: "Most swishes"
				},
				bank_artist: {
					title: "Bank Artist",
					description: "Most bank shots"
				},
				half_court_hero: {
					title: "Half Court Hero",
					description: "Most long range shots"
				}
			},
			generic_tag: {
				took_nap: {
					title: "Took a nap",
					description: "Barely moved"
				},
				stealthy: {
					title: "Stealthy",
					description: "Never tagged"
				},
				speedster: {
					title: "Speedster",
					description: "Used the most speed boosts"
				},
				bouncy: {
					title: "Bouncy",
					description: "Used springy surfaces"
				},
				lookout: {
					title: "On the lookout",
					description: "Used camera controls the most"
				},
				grounded: {
					title: "Grounded",
					description: "Jumped the least"
				},
				hopper: {
					title: "Hopper",
					description: "Jumped the most"
				},
				on_move: {
					title: "On the move",
					description: "Moved the most"
				},
				swimmer: {
					title: "Swimmer",
					description: "Most time in the water"
				},
				climber: {
					title: "Climber",
					description: "Most time on ladders"
				},
				launcher: {
					title: "Launcher",
					description: "Launched from the most cannons"
				}
			},
			blastsplash: {
				artiste: {
					title: "Artiste",
					description: "Painted the most blocks"
				},
				repainter: {
					title: "Repainter",
					description: "Repainted the most blocks"
				},
				cannoneer: {
					title: "Cannoneer",
					description: "Most cannon shots"
				}
			},
			paintparty: {
				mixologist: {
					title: "Mix Wiz",
					description: "Most paints mixed"
				},
				cleaner: {
					title: "Swisher",
					description: "Cleaned brush the most"
				},
				littledipper: {
					title: "Little Dipper",
					description: "Changed colors the least"
				},
				bigdipper: {
					title: "Big Dipper",
					description: "Changed colors the most"
				}
			},
			monsterball: {
				striker: {
					title: "Striker",
					description: "Kicked the most balls"
				},
				goldenboot: {
					title: "Golden Boot",
					description: "Scored the most goals"
				},
				attacker: {
					title: "Attacker",
					description: "Most time in front of center line"
				},
				defender: {
					title: "Defender",
					description: "Most time behind center line"
				},
				goaltender: {
					title: "Goaltender",
					description: "Most time near own goal"
				},
				onfire: {
					title: "On Fire",
					description: "Used the most speed boosts"
				},
				marathoner: {
					title: "Marathoner",
					description: "Ran the furthest"
				}
			},
			dolphinpolo: {
				playmaker: {
					title: "Playmaker",
					description: "Most touches"
				},
				goldenflipper: {
					title: "Golden Flipper",
					description: "Top goal scorer"
				},
				goalkeeper: {
					title: "Goalkeeper",
					description: "Most time near own goal"
				},
				ultra_swimmer: {
					title: "Ultra Swimmer",
					description: "Swam the furthest"
				},
				jumper: {
					title: "Jumper",
					description: "Dolphin jumped the most"
				}
			}
		}
	},
	pocket_world: {
		entered: "__name__'s World",
		cannot_join_in_activity: "Cannot Join While in Activity",
		instructions: "BUILD A TAG MAP!",
		nameplate: "__name__'s BUILD ZONE",
		owner_left: "Gateway closed",
		gateway_not_allowed_here: "Can't put Gateway here",
		gateway_too_close: "Too close to another Gateway",
		coop_enabled_owner: "Team build ON",
		coop_disabled_owner: "Team build OFF",
		coop_enabled_guest: "Build with __name__ ON",
		coop_disabled_guest: "Build with __name__ OFF"
	},
	errors: {
		defaults: {
			title: "Error",
			ok: "Ok",
			cancel: "Cancel"
		},
		idle: {
			title: "Your monster fell asleep!",
			yes: "Wake them up!",
			no: "Exit"
		},
		other: {
			title: "Your island is taking a quick break.",
			description: "Come back later!",
			yes: "Try one more time",
			no: "Exit"
		},
		connection_timeout: {
			title: "We didn't hear back from the island!",
			description: "Come back later and try again!",
			yes: "Try again now",
			no: "Exit"
		},
		ticket_expired_timeout: {
			title: "It took too long to get you to the island!",
			description: "Make sure you have a strong internet connection, and try again.",
			yes: "Try again now",
			no: "Exit"
		},
		websocket_closure_1: {
			title: "We had trouble getting you to the island!",
			description: "Check your internet connection and try again!\n\nThis will help us to figure out problems.",
			yes: "Try again now",
			no: "Exit"
		},
		websocket_closure_2: {
			title: "We're still having trouble getting you to the island!",
			description: "You may need to ask for help!\nShow the below link to your teacher or parent!",
			help_page_link: "ClassDojo's \"Domains to Allow List\"",
			help_page_ref: "Ask if this address is blocked, or if your network is having issues:",
			yes: "Try one more time",
			no: "Exit"
		},
		removed: {
			title: "Oops!",
			description: "Your monster joined another island.\n\nYou can only play on a single island at a time.",
			yes: "Exit"
		},
		not_logged_in: {
			title: "You are not logged in.",
			description: "This page requires a student or teacher login.\n\nPlease log in and try again!",
			yes: "Exit"
		},
		incompatible_browser: {
			title: "Browser is incompatible.",
			description: "Please try updating it to a more recent version.",
			no: "Exit"
		},
		incompatible_os: {
			title: "Incompatible OS Version",
			description: "Please update your OS to the most recent version to load the island.",
			no: "Exit"
		},
		webgl_context_failed_init: {
			title: "WebGL failed to initialize.",
			description: "Please try closing your application and restarting your device. If that fails, please contact ClassDojo support.",
			no: "Exit"
		},
		webgl_support: {
			title: "This device can't run Dojo Islands",
			description: "This page requires a browser that supports WebGL. Please upgrade your operating system or use a different device.",
			link_text: "Click here to find out more.",
			yes: "Exit"
		},
		webgl2_support: {
			ios: {
				title: "This device can't run Dojo Islands",
				description: "You must upgrade to iOS 15, 16 or 17 to play",
				link_text: "Click here to find out how",
				yes: "Exit"
			},
			macos: {
				title: "This device can't run Dojo Islands",
				description: "You must either upgrade to Safari 15+ or macOS Monterey to play",
				link_text: "Click here to find out how",
				yes: "Exit"
			},
			"default": {
				title: "This device can't run Dojo Islands",
				description: "Please upgrade your operating system or use a different device.",
				yes: "Exit"
			}
		},
		outage: {
			title: "Sorry!",
			description: "We are experiencing an issue and are working to fix the problem.",
			no: "Exit"
		},
		bundle_not_found: {
			title: "Island scene not found",
			description: "• Check the URL\n\n• Make sure that your island was published successfully",
			no: "Exit"
		},
		bundle_load_error_1: {
			title: "We had trouble loading your island!",
			description: "Check your internet connection and try again!\n\nThis will help us to figure out problems.",
			yes: "Try Again",
			no: "Exit"
		},
		bundle_load_error_2: {
			title: "We still have trouble loading your island!",
			description: "Click and show the below link to your teacher or parent.",
			help_page_link: "ClassDojo's \"Domains to Allow List\"",
			help_page_ref: "Ask if this address is blocked, or if your network is having issues:",
			yes: "Try Again",
			no: "Exit"
		},
		inventory_error: {
			description: "We're having trouble loading your inventory. Blocks might be missing temporarily. Retrying..."
		}
	}
};
var emotes = {
	hi: "Hi!",
	yes: "Yes",
	no: "No",
	thanks: "Thanks!",
	sorry: "Sorry",
	lets_play: "Let's play!",
	wait_for_me: "Wait for me!",
	nice_outfit: "Nice outfit!",
	goodbye: "Goodbye",
	have_to_go: "I have to go!",
	bye: "Bye!",
	help_please: "Help please",
	cool: "Cool!",
	follow_me: "Follow me!",
	boo: "Boo!",
	eek: "Eek!",
	trick_or_treat: "Trick or treat!",
	ok: "OK",
	im_grateful: "I'm grateful",
	snowball_fight: "Snowball fight!",
	yay: "Yay!",
	happy_dance: "Happy Dance!"
};
var activity_zone = {
	ui: {
		sign_top: "Activity Zone",
		title: "Teacher's Choice Activity",
		help_title: "Where is the Activity of the Month?",
		help_text: "Activity of the Month will be back next month. Or, you can ask your teacher to switch back when your class is done with the assigned activity.",
		title_aotm: "Activity of the Month",
		help_title_aotm: "Can't find your build?",
		help_text_aotm: "It might be saved in an earlier activity. The activity of the month changes on the 1st, but your teacher can switch it anytime.",
		close_modal_btn: "Got it"
	}
};
var math_zone = {
	ui: {
		sign_top: "Number Shores",
		prompt: "Find and solve equations as a team!"
	}
};
var arena_zone = {
	ui: {
		sign_top: "Arena"
	}
};
var building_zone = {
	ui: {
		reset_zone: {
			button: "Reset",
			resetZoneTitle: "Reset a zone?",
			description: "This will remove all blocks placed for all users.",
			resetOG: "Clear Building Zone",
			no: "Cancel",
			resetAZ: "Reset Activity Zone"
		},
		edit_zones: {
			button: "Zones",
			title: "Change Zoning",
			description: "How many zones would you like the Building Zone divided into? This will not remove any blocks currently in the Building Zone.",
			none: "One",
			two: "Two",
			four: "Four",
			cancel: "Cancel"
		},
		build_buttons: {
			build: "BUILD"
		},
		creative_mode: {
			entered: "Flying camera ON",
			exited: "Flying camera OFF"
		},
		block_of_the_month: "BLOCK OF THE MONTH",
		blocks_of_the_month: "BLOCKS OF THE MONTH"
	}
};
var gather_loop = {
	notifications: {
		new_block: "New Block!",
		reward_at_tent: "Reward at the Coconut Tent!",
		race_go: "🏞️ 🚦 Go! 🦦⭐️",
		race_times_up: "🏁 Time's up! ✅",
		unlocked: "New! __unlock__!",
		portal_switch_1_pressed: "🤔 Find all 5, portals come alive. 🤔",
		portal_switch_2_pressed: "🤔 3 more plates, to open the gates. 🤔",
		portal_switch_3_pressed: "🤔 2 more to press, to end their rest. 🤔",
		portal_switch_4_pressed: "🤔 Just 1 more, to open the door. 🤔",
		portals_unlocked: "🌀 The portals are open! 🌀"
	},
	timed_collect_hub: {
		gather_flowers: "🌼 Gather flowers! 🌼",
		try_again: "🌼 Try again, faster! 🏃‍♀️",
		yay: "🌼 Yay! 🥥"
	},
	timed_collect_hub_hard: {
		gather_flowers: "🌺 Gather flowers! 🌺",
		try_again: "🌺 Try again, faster! 🏃‍♀️",
		yay: "🌺 Yay! 🥥"
	},
	converter1: {
		tent_party: "🎉 A party is starting! 🎉"
	},
	ConverterSlime1: {
		slime_ready: "💚🦠 Slime ready! 🧪🟢"
	}
};
var items = {
	Reward_Block_Yellow: "🟨 Yellow Block",
	Reward_Block_Red: "🟥 Red Block",
	Reward_Block_Blue: "🟦 Blue Block",
	Reward_Block_Green: "🟩 Green Block",
	Reward_Block_Purple: "🟪 Purple Block",
	Reward_Block_Orange: "🟧 Orange Block",
	Reward_Block_Black: "⬛ Black Block",
	Reward_Block_Pink: "🩷 Pink Block",
	Reward_Block_LightBlue: "🐳 Light Blue Block",
	Reward_Block_Brown: "🟫 Brown Block",
	Reward_Block_White: "⬜ White Block",
	Reward_Block_Water: "💧 Water Block",
	Reward_Block_Straw: "🌾 Straw Block",
	Reward_Block_Brick: "🧱 Brick Block",
	Reward_Block_WoodPlanks: "🪵 Wood Block",
	Reward_Block_Ice: "🧊 Ice Block",
	Reward_Block_Bounce: "🦘 Bounce Block",
	Reward_Block_Speed: "🏎️ Speed Block",
	Reward_Block_Cardboard: "📦 Cardboard Block",
	Reward_Block_Swamp: "🐊 Swamp Block",
	Reward_Block_Bookshelves: "📚 Bookshelf Block",
	Reward_Block_Knitted: "🧶 Knitted Block",
	Reward_Block_RuinStone: "🪨 Stone Block",
	Reward_Block_Ladder: "🪜 Ladder Block",
	Reward_Block_Marble: "🗿 Marble Block",
	Reward_Block_Quilted: "🧶 Quilted Block",
	Reward_Block_Glass: "🪞 Glass Block",
	Reward_Block_Metal: "🪙 Metal Block",
	Reward_Block_PaintedPark: "🖼️ Painted Park Block",
	Reward_Block_Gray: "🩶 Gray Block"
};
var snap_and_share = {
	download: "Download to Share",
	post_to_class_story: "Post to Class Story",
	posted_to_class_story: "Posted to Class Story!",
	add_frame: "Add Valentine's Frame",
	add_caption: "Add a caption"
};
var customizer = {
	ui: {
		"new": "new",
		unlock: "Unlock"
	},
	errors: {
		old_classdojo_app: "Whoops! Looks like you are using an older ClassDojo app.\nPlease update to get the latest features.",
		webgl_support: "The monster customizer requires a browser that supports WebGL2. Please upgrade your operating system or use a different device.",
		unknown: "Whoops! Looks like something went wrong."
	},
	rewards: {
		new_monster_parts: "New monster parts!"
	}
};
var home = {
	notifications: {
		island: "Your island",
		zone: "Your zone",
		other_zone: "__name__'s zone",
		joined_island: "__name__ has arrived",
		joined_other_island: "__name__ just landed on __island__"
	},
	warp: {
		start: "Warp to starting area",
		activity_zone: "Warp to Activity Zone",
		math_zone: "Warp to Number Shores",
		arena_zone: "Warp to Arena",
		all_activity_zone: "Warp students to Activity Zone",
		all_math_zone: "Warp students to Number Shores",
		all_arena_zone: "Warp students to Arena",
		hub: "Warp to shippelin dock",
		island: "Warp to your island",
		zone: "Warp to your zone",
		arena: "Warp to arena",
		playground: "Warp to global playground",
		pw_exit: "Warp to the exit",
		inviter_zone: "Warp to __name__'s zone",
		locate_player: "Where is __name__?",
		finding_player: "Going to __name__",
		in_playgrounds: "__name__ in Global Playground",
		travel_to: "Travel to __island__",
		join_one: "Join __name1__",
		join_two: "Join __name1__ and __name2__",
		join_three: "Join __name1__, __name2__, and __name3__",
		join_more: "Join __name1__, __name2__, and __number__ others",
		join_confirm: "Join __island__?"
	},
	block_vendors: {
		trade: "TRADE",
		not_enough_seashells: "__number1__/__number2__",
		come_back_tomorrow: "More tomorrow",
		awarded_blocks: "__number1__ __string1__\n\n\n__number2__ __string2__",
		daily_awards: "BLOCKS OF THE DAY",
		wombats_gift: "Wombat's Gift",
		ladder_Block: "Ladder",
		blue_Block: "Blue",
		woodPlanks_Block: "Wood",
		water_Block: "Water",
		green_Block: "Green",
		brick_Block: "Brick",
		dirt_Block: "Dirt",
		straw_Block: "Straw",
		white_Block: "White",
		bookshelves_Block: "Bookshelf",
		black_Block: "Black",
		speed_Block: "Speed",
		bounce_Block: "Bounce",
		ice_Block: "Ice",
		glass_Block: "Glass",
		pink_Block: "Pink"
	}
};
var welcome = {
	home_island: "HOME ISLAND",
	class_island: "CLASS ISLAND"
};
var menu = {
	report: "Report",
	leave_island: "Leave island"
};
var ugc_reporting = {
	title: "Send report to ClassDojo",
	description: "Tell us why you're reaching out and share what is happening. We may reach out to your adults for more information",
	submitted: "Report submitted",
	error: "Something went wrong, please try again",
	report_time_limit: {
		title: "Report submitted.",
		description: "If you wish to submit another report, please wait a few minutes."
	},
	categories: {
		placeholder: "Pick one",
		something: "Report something",
		someone: "Report someone"
	},
	target_player: {
		something_placeholder: "Whose zone?",
		someone_placeholder: "Who?"
	},
	issues: {
		bullying: "Someone being mean / bullying",
		unknown_player: "Someone who should not be here",
		dont_like_this: "I just don't like this",
		bad_words: "Bad words",
		offensive_symbol: "Offensive symbol"
	},
	actions: {
		back: "Go back",
		cancel: "Cancel",
		close: "Close",
		submit: "Submit"
	}
};
var teacher_ftue = {
	welcome_heading: "WELCOME, __teacherName__!",
	welcome_intro: "Come on in and take a look around.",
	welcome_description: "Your private class island is full of fun areas where your kids can explore, build, and learn together.",
	number_shores_prompt: "Be sure to check out __game__, our math adventure game. We think you and your class will love it!",
	number_shores_name: "NUMBER SHORES",
	start_playing: "Start Playing",
	got_it: "Got it",
	click_to_move: "CLICK TO MOVE",
	space_to_jump: "SPACE TO JUMP",
	tap_to_move: "TAP TO MOVE",
	d_pad: "OR USE DPAD"
};
var student_ftue = {
	characters: {
		shelby: {
			name: "Ranger Tumbles"
		},
		spritz: {
			name: "Ranger Spritz"
		}
	},
	ftue_movement: {
		objective1: {
			text: "Check out that rock"
		},
		intro: {
			title: "Go to the campfire",
			text: "Let's cook some marshmallows!",
			button_accept: "Okay",
			button_decline: "Maybe later"
		},
		touch_input: "Touch input detected! Following touch-optimized quest path.",
		mouse_input: "Mouse input detected! Following mouse-optimized quest path.",
		decline_message: "I guess you're not hungry. Try the quest later.",
		npc_talks_to_player: {
			title: "Thank You",
			text: "I thought I was going to be stuck\nlike that FOREVER. Thank goodness\nyou came along!\n\nI'm Ranger Tumbles.\nPleased to meet you!",
			button: "HELLO!"
		},
		ftue_welcome_dojo_islands: {
			title: "Welcome to Dojo Islands!\nYou can explore, build, and play games.",
			text: "",
			button: "Okay!"
		},
		complete: "Quest Complete!"
	},
	ftue_go_to_playground: {
		objective1: {
			text: "Meet Ranger Spritz"
		},
		teach_playgrounds_intro: {
			title: "You should go meet Ranger Spritz!\nShe's nice, smells weird, and LOVES\nmaking new friends. Go say hi!",
			text: "",
			button: "Okay!"
		},
		reached_playground: {
			desc: "Hi, I'm Ranger Spritz!\nThis... is the Playground!\n\nRun up to that sign to join a game.",
			button: "Nice!"
		},
		reached_arena: {
			desc: "Hi, I'm Ranger Spritz! This is the Arena,\nwhere you can play fun games with friends.\n\nYou can run to the sign to pick a game.",
			button: "Nice!"
		}
	},
	ftue_go_to_rpc: {
		objective1: {
			text: "Go in the Chicken Tent"
		},
		intro: {
			title: "Speaking of play...you should check out\nthat tent behind me!\n\nLook for the chicken on top.",
			button: "Okay!"
		},
		outro: {
			title: "You found a costume tent!\n\nThey're great for playing dress up.",
			button: "Nice"
		}
	},
	ftue_explore_rpc: {
		objective1: {
			text: "Explore the Roleplay Corner"
		},
		intro: {
			title: "And just past that water\nis the Roleplay Corner.\n\nTry exploring and seeing what you can do!",
			button: "Okay!"
		},
		outro: {
			title: "Had fun?\n\nWell, now you're free to explore the entire island! Enjoy yourself!",
			button: "Okay!"
		}
	},
	ftue_rpc_to_turtle: {
		objective1: {
			text: "Return to Ranger Tumbles"
		},
		intro: {
			title: "Had fun exploring?\nWell, there's even more fun stuff to play!\n\nCome back to me, and I'll show you!",
			button: "Okay!"
		}
	}
};
var share_links = {
	label: "Island code",
	title: "Invite friends and play!",
	subtitle: "Your friends can scan this code to play with you. Code works for a limited time.",
	copy_link: "Copy",
	error_generating_link: "There was an error generating the link. Please try again.",
	or_share_invite_link: "Or share invite link"
};
var open_signup = {
	sign_up_button: "Sign Up",
	log_in_with_code_button: "Log in with code",
	login_modal: {
		title: "Enter your parent's email",
		subtitle: "to get a login code.",
		api_something_went_wrong: "Whoops! Looks like something went wrong. Please try again",
		parent_email_label: "Parent email",
		parent_email_error: "Please provide a valid email address",
		parent_email_suggest: "Did you mean __email__?",
		continue_button: "Continue",
		already_have_code: "Already have a code? <u>Click here</u>",
		waitlist_confirmation: "You're on the waitlist!",
		waitlist_confirmation_subtitle: "We'll let you know as soon as sign-ups are open.",
		existing_teacher_account: "Please use a different email",
		existing_teacher_account_subtitle: "This email is already used by a ClassDojo Teacher account.",
		existing_teacher_account_login: "Did you mean to sign in as a teacher? Go <a href=\"https://teach.classdojo.com/\" target=\"_blank\" rel=\"noreferrer\">here</a>"
	},
	login_modal_with_code: {
		title: "Enter Email & Code",
		code_label: "Login code",
		parent_code_error: "Invalid code: it should have 6 digits",
		incorrect_code: "Incorrect code. Check and try again."
	},
	one_time_code_modal: {
		title: "Welcome back",
		subtitle: "Enter the login code sent to __parent_email__",
		title_consent: "Ask your parent to check their email",
		subtitle_consent: "We emailed __parent_email__. You'll get a code once they finish.",
		resend: "Resend code",
		resend_now: "Didn't receive a code? <u>Resend now</u>",
		sending: "Sending...",
		incorrect_code: "Incorrect code. Check and try again, or request a new one."
	},
	one_time_code_modal_for_new_account: {
		title: "Ask your parent to check their email",
		subtitle: "We emailed __parent_email__. You'll get a code once they finish.",
		incorrect_code: "Incorrect code. Check and try again."
	}
};
var dojo_monsterverse = {
	common: common,
	emotes: emotes,
	activity_zone: activity_zone,
	math_zone: math_zone,
	arena_zone: arena_zone,
	building_zone: building_zone,
	gather_loop: gather_loop,
	items: items,
	snap_and_share: snap_and_share,
	customizer: customizer,
	home: home,
	welcome: welcome,
	menu: menu,
	ugc_reporting: ugc_reporting,
	teacher_ftue: teacher_ftue,
	student_ftue: student_ftue,
	share_links: share_links,
	open_signup: open_signup
};

var latestTranslations = {
  __proto__: null,
  common: common,
  emotes: emotes,
  activity_zone: activity_zone,
  math_zone: math_zone,
  arena_zone: arena_zone,
  building_zone: building_zone,
  gather_loop: gather_loop,
  items: items,
  snap_and_share: snap_and_share,
  customizer: customizer,
  home: home,
  welcome: welcome,
  menu: menu,
  ugc_reporting: ugc_reporting,
  teacher_ftue: teacher_ftue,
  student_ftue: student_ftue,
  share_links: share_links,
  open_signup: open_signup,
  'default': dojo_monsterverse
};

const DEFAULT_LOCALE = "en";
const SUPPORTED_LOCALES = [
// "af-ZA",
// "am-ET",
"ar-SA",
// "be-BY",
// "bg-BG",
// "bn-BD",
"ca-ES", "cs-CZ",
// "cy-GB",
"da-DK", "de-DE",
// "el-GR",
"en", "en-GB", "en-US", "es-ES", "es-MX",
// "et-EE",
// "eu-ES",
// "fa-IR",
"fi-FI", "fil-PH", "fr-CA", "fr-FR",
// "ga-IE",
// "gl-ES",
// "he-IL",
// "hi-IN",
"hr-HR",
// "hu-HU",
// "id-ID",
"it-IT", "ja-JP",
// "kk-KZ",
"ko-KR",
// "lt-LT",
// "lv-LV",
// "ml-IN",
"ms-MY", "nb-NO", "nl-NL",
// "pa-IN",
"pl-PL", "pt-BR", "pt-PT",
// "ro-RO",
"ru-RU",
// "sk-SK",
// "sl-SI",
// "sr-SP",
"sv-SE",
// "sw-KE",
// "ta-IN",
// "te-IN",
"th-TH",
// "tlh-AA",
"tr-TR", "uk-UA",
// "ur-PK",
"vi-VN", "zh-CN", "zh-HK", "zh-TW"
// "zu-ZA",
// "zz-ZZ",
];
const FALLBACKS = {
  "es-MX": ["es-ES", "en"],
  // Mexican Spanish
  "es-ES": ["es-MX", "en"],
  // Spanish Spanish
  "fr-CA": ["fr-FR", "en"],
  // Canadian French
  "fr-FR": ["fr-CA", "en"],
  // French French
  "zh-CN": ["zh-HK", "zh-TW", "en"],
  "zh-TW": ["zh-HK", "zh-CN", "en"],
  "zh-HK": ["zh-TW", "zh-CN", "en"],
  ca: ["es-ES", "es-MX", "en"],
  // Catalan
  default: ["en"]
};
let _isLocalEnv = false;
let _logger = () => {
  // NOOP
};
let initStatus = "NOT_INIT";
// Example URL that fetches strings we need:
// https://teach.classdojo.com/locales/resources.json?lng=en+en-US&ns=dojo.monsterverse+dojo.dojo_islands_activities
const namespaces = ["dojo.monsterverse", "dojo.dojo_islands_activities"];
async function initializeTranslations(locale, {
  apiEndpoint,
  build,
  isLocal = false,
  logger = () => {
    // NOOP
  }
} = {}) {
  if (i18next.isInitialized) {
    return;
  }
  _isLocalEnv = isLocal;
  try {
    initStatus = "BEFORE_INIT";
    await i18next.use(BackendAdapter).init(_extends({
      ns: namespaces,
      defaultNS: namespaces[0],
      lng: mapLocaleToI18nLocale(locale || DEFAULT_LOCALE),
      fallbackLng: FALLBACKS,
      interpolation: {
        prefix: "__",
        suffix: "__",
        escapeValue: false // not needed for react as it escapes by default
      }
    }, _isLocalEnv ? {
      resources: {}
    } : {
      backend: {
        backend: new Backend(),
        backendOption: {
          allowMultiLoading: true,
          crossDomain: true,
          loadPath: `${apiEndpoint || ""}/locales/resources.json?lng=__lng__&ns=__ns__&build=${build || Date.now()}`
        }
      }
    }));
    initStatus = "AFTER_INIT";
  } catch (err) {
    const message = err instanceof Error ? err.message : "";
    const stack = err instanceof Error ? err.stack : "";
    logger("warn", `[i18n] Error initializing i18next ${message} :: ${stack}".`);
  } finally {
    // replace any loaded "en" translations with latest translations from repo source-of-truth
    i18next.addResourceBundle(DEFAULT_LOCALE, namespaces[0], latestTranslations);
    initStatus = "EN_LOADED";
    _logger = logger;
  }
}
/**
 * This function covers edge cases regarding what locale actually gets fetched via i18next
 */
function mapLocaleToI18nLocale(locale) {
  if (!isSupportedLocale(locale)) {
    // use default locale if specified locale is currently not supported
    return DEFAULT_LOCALE;
  }
  if (locale === "en-US") {
    // en-US and en are actually the same, because the source of the translations ("en")
    // is written by people in the US. Rewrite requests from "en-US" to "en" so we don't
    // fetch two bundles with the same data. Also because test environments only know how
    // to serve "en".
    return DEFAULT_LOCALE;
  }
  return locale;
}
// This function is used to check if a translation key is valid without firing off any logs for failures
function isValidTranslationKey(str, subs) {
  const output = subs ? i18next.t(str, subs) : i18next.t(str);
  return output != undefined && output !== str;
}
function translate(str, subs) {
  const fallback = _isLocalEnv && str || "";
  if (!str) {
    _logger("error", `[i18n] Missing translation key`, {
      stack: new Error().stack
    });
    return fallback;
  }
  const output = subs ? i18next.t(str, subs) : i18next.t(str);
  if (!output || output === str) {
    const debug = {
      initStatus,
      output,
      str,
      language: i18next.language,
      isInitialized: i18next.isInitialized,
      hasLoadedNamespace: i18next.hasLoadedNamespace == null ? void 0 : i18next.hasLoadedNamespace(namespaces[0]),
      hasENResourceBundle: i18next.hasResourceBundle == null ? void 0 : i18next.hasResourceBundle(DEFAULT_LOCALE, namespaces[0])
    };
    _logger("warn", `[i18n] Missing translation for key "${str}". Debug:${JSON.stringify(debug)}\nWas it added to "dojo.monsterverse.json"?`);
    return fallback;
  }
  return processTextForDisplayInPlaycanvasTextElement(output);
}
/**
 * Use for text that doesn't use translations, e.g. player names.
 *
 * If setting playcanvas element text without going through `translate()` and
 * using the returned translated string, we need to call this to setup
 * the text/font for display in some of the supported locales.
 *
 * Also initializes the CanvasFont for languages that need it, if not already done.
 */
function processTextForDisplayInPlaycanvasTextElement(text) {
  if (isArabic()) {
    // Convert from Arabic to Arabic Presentation Forms-B
    return convertArabic(text);
  }
  if (supportsCanvasFont(getLanguage())) {
    initializeCanvasFont();
    updateCanvasFontCharacters(text);
  }
  return text;
}
function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale);
}
function createDynamicTranslation(translationKey, subs) {
  let translationEntry = latestTranslations;
  const splitTranslations = translationKey.split(".");
  for (const entry of splitTranslations) {
    if (typeof translationEntry !== "object" || !(entry in translationEntry)) {
      _logger("error", `[i18n] Failed to create dynamic translation for key ${translationKey}`);
      break;
    }
    translationEntry = translationEntry[entry];
  }
  return translate(translationKey, subs);
}

export { DEFAULT_LOCALE, createDynamicTranslation, initializeTranslations, isRTLLocale, isValidTranslationKey, playcanvasRTLReorder, playcanvasSetupLanguageFontsSupport, processTextForDisplayInPlaycanvasTextElement, translate };
//# sourceMappingURL=index.modern.js.map
